import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { tmsCarriersApi } from '@/api/services';

/**
 * Which carrier is moving this, as a record where possible.
 *
 * The field was a free-text box, and the text it collected did not line up with
 * the carrier records: "Maersk" beside a "Maersk Line", a bare "FedEx" beside a
 * "FedEx Freight" it is not. Nothing linked them, so a shipment could not be
 * joined to the carrier's lanes, rates or performance.
 *
 * Picking a carrier sets both the link and the name, so the text stays true to
 * the record rather than drifting from it. The free-text option remains, because
 * a one-off haulier that is not on the books is a real thing and refusing to
 * record it would send somebody to a spreadsheet.
 */

const OTHER = '__other__';

export function CarrierField({
  carrierId,
  carrier,
  onChange,
}: {
  carrierId: string | null;
  carrier: string;
  onChange: (next: { carrierId: string | null; carrier: string }) => void;
}) {
  const [carriers, setCarriers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [freeText, setFreeText] = useState(!carrierId && !!carrier);

  useEffect(() => {
    tmsCarriersApi
      .getAll()
      .then((res) => setCarriers(res.data.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => setCarriers([]))
      .finally(() => setLoading(false));
  }, []);

  const selected = freeText ? OTHER : (carrierId ?? '');

  return (
    <div className="space-y-2">
      <Label htmlFor="carrier">Carrier</Label>
      <Select
        value={selected || undefined}
        onValueChange={(v) => {
          if (v === OTHER) {
            setFreeText(true);
            // The link goes when the record does. Leaving it behind is how a
            // shipment ends up naming one carrier and pointing at another.
            onChange({ carrierId: null, carrier });
            return;
          }
          setFreeText(false);
          const picked = carriers.find((c) => c.id === v);
          onChange({ carrierId: v, carrier: picked?.name ?? '' });
        }}
      >
        <SelectTrigger id="carrier">
          <SelectValue placeholder={loading ? 'Loading carriers…' : 'Choose a carrier'} />
        </SelectTrigger>
        <SelectContent>
          {carriers.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
          <SelectItem value={OTHER}>Not on the list</SelectItem>
        </SelectContent>
      </Select>

      {freeText && (
        <div className="space-y-1">
          <Input
            placeholder="Carrier name"
            value={carrier}
            onChange={(e) => onChange({ carrierId: null, carrier: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Recorded as text and not linked to a carrier record, so this shipment will not appear
            against that carrier&rsquo;s lanes, rates or performance.
          </p>
        </div>
      )}
    </div>
  );
}
